import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  ClipboardList,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Phone,
  Search,
  ShoppingCart,
  Truck,
  User,
  Wrench,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useDeskCustomer,
  useDeskSearch,
  type DeskCustomer,
  type DeskOrder,
  type DeskSearchResult,
} from '@/hooks/usePhoneDesk';
import { DeskOrderPanel } from './DeskOrderPanel';
import { DeskServicePanel } from './DeskServicePanel';
import { fmtDay, fmtDateTime, orderStatusLabel, serviceKindLabel } from './deskFormat';

/**
 * /desk — the phone desk. A live receptionist is on a call: find the
 * customer, confirm who they are talking to, answer "where is my order", place
 * an order or open a service request. Built for the keyboard:
 *
 *   /        focus the search box        ↑ ↓ Enter  pick a result
 *   n        new order                   s          service request
 *   Esc      close the panel, then back to search
 *
 * State lives in the URL (?c=customer&loc=location&panel=order|service) so a
 * refresh or the browser's back button never loses the caller.
 */

function isTyping(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
}

export default function Desk() {
  const [params, setParams] = useSearchParams();
  const customerId = params.get('c');
  const panel = params.get('panel') as 'order' | 'service' | null;
  const locParam = params.get('loc');

  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [cursor, setCursor] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q), 300);
    return () => window.clearTimeout(t);
  }, [q]);

  const search = useDeskSearch(debounced);
  const results = useMemo(() => (debounced.trim().length >= 2 ? search.data ?? [] : []), [search.data, debounced]);
  useEffect(() => setCursor(0), [debounced]);

  const detail = useDeskCustomer(customerId);

  function update(next: Record<string, string | null>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v == null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p);
  }
  function openCustomer(r: DeskSearchResult) {
    update({ c: r.id, loc: r.location_id, panel: null });
  }
  function backToSearch() {
    update({ c: null, loc: null, panel: null });
    window.setTimeout(() => searchRef.current?.focus(), 0);
  }

  // Global keys.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Escape') {
        if (panel) update({ panel: null });
        else if (customerId && !isTyping(e.target)) backToSearch();
        return;
      }
      if (isTyping(e.target)) return;
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      } else if (customerId && (e.key === 'n' || e.key === 'N')) {
        e.preventDefault();
        update({ panel: 'order' });
      } else if (customerId && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        update({ panel: 'service' });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel, customerId, params]);

  function onSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter' && results[cursor]) {
      e.preventDefault();
      openCustomer(results[cursor]!);
      searchRef.current?.blur();
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[380px_minmax(0,1fr)]">
      {/* ── Search column (hidden on a phone once a customer is open) ── */}
      <section className={cn('min-w-0 space-y-3', customerId && 'hidden lg:block')} aria-label="Find a customer">
        <div className="rounded-xl border border-brix-border bg-brix-surface p-3 shadow-card">
          <label htmlFor="desk-search" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brix-muted">
            Find the caller's account
          </label>
          <div className="relative">
            <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted" aria-hidden />
            <input
              id="desk-search"
              ref={searchRef}
              autoFocus
              autoComplete="off"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Name, phone, email, account # or address"
              className="h-12 w-full rounded-lg border border-brix-border bg-brix-surface pl-10 pr-3 text-base text-brix-ink placeholder:text-brix-muted focus:border-brix-primary focus:outline-none focus:ring-2 focus:ring-brix-primary/20"
            />
          </div>
          <p className="mt-1.5 text-[11px] text-brix-muted">
            <kbd className="rounded border border-brix-border px-1 font-mono">/</kbd> search ·{' '}
            <kbd className="rounded border border-brix-border px-1 font-mono">↑↓</kbd>{' '}
            <kbd className="rounded border border-brix-border px-1 font-mono">Enter</kbd> open ·{' '}
            <kbd className="rounded border border-brix-border px-1 font-mono">Esc</kbd> back
          </p>
        </div>

        <div className="overflow-hidden rounded-xl border border-brix-border bg-brix-surface shadow-card">
          {debounced.trim().length < 2 ? (
            <p className="px-4 py-8 text-center text-sm text-brix-muted">
              Ask for the business name or the number they are calling from.
            </p>
          ) : search.isLoading ? (
            <p className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-brix-muted">
              <Loader2 size={16} className="animate-spin" /> Searching…
            </p>
          ) : search.isError ? (
            <p className="px-4 py-8 text-center text-sm text-brix-warning">Search failed — try again.</p>
          ) : results.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-brix-muted">
              No account matches “{debounced.trim()}”. Try the street address or the phone number.
            </p>
          ) : (
            <ul role="listbox" aria-label="Matching accounts" className="divide-y divide-brix-border">
              {results.map((r, i) => (
                <li key={r.id} role="option" aria-selected={r.id === customerId}>
                  <button
                    type="button"
                    onClick={() => openCustomer(r)}
                    onMouseEnter={() => setCursor(i)}
                    className={cn(
                      'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
                      r.id === customerId ? 'bg-brix-primary-tint' : i === cursor ? 'bg-brix-surface-alt' : 'hover:bg-brix-surface-alt',
                    )}
                  >
                    <Building2 size={16} className="mt-0.5 shrink-0 text-brix-primary" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-brix-ink">{r.name}</span>
                      <span className="block truncate text-xs text-brix-muted">
                        {[
                          r.qbo_customer_id != null ? `#${r.qbo_customer_id}` : null,
                          r.city,
                          r.location_count > 1 ? `${r.location_count} locations` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                      {r.matched && <span className="mt-0.5 block truncate text-xs text-brix-primary">{r.matched}</span>}
                    </span>
                    {r.ordering_paused && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded border border-brix-warning/40 px-1.5 py-0.5 text-[10px] font-semibold text-brix-warning">
                        <Lock size={10} aria-hidden /> Paused
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ── Customer column ── */}
      <section className={cn('min-w-0', !customerId && 'hidden lg:block')} aria-label="Customer">
        {!customerId ? (
          <div className="flex h-full min-h-[320px] flex-col items-center justify-center rounded-xl border border-dashed border-brix-border bg-brix-surface/60 px-6 text-center">
            <Phone size={28} className="text-brix-primary" aria-hidden />
            <p className="mt-3 font-display text-base font-semibold text-brix-ink">Pick up, then search</p>
            <p className="mt-1 max-w-sm text-sm text-brix-muted">
              Find the caller's account on the left. You can see their orders, deliveries and
              service requests, place an order, or open a service request.
            </p>
          </div>
        ) : detail.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-24 text-brix-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading account…</span>
          </div>
        ) : detail.isError || !detail.data ? (
          <div className="rounded-xl border border-brix-border bg-brix-surface p-6 text-center text-sm text-brix-warning">
            Could not load this account.{' '}
            <button type="button" className="underline" onClick={backToSearch}>Back to search</button>
          </div>
        ) : (
          <CustomerView
            data={detail.data}
            locationId={locParam}
            panel={panel}
            onPanel={(p) => update({ panel: p })}
            onLocation={(id) => update({ loc: id })}
            onBack={backToSearch}
          />
        )}
      </section>
    </div>
  );
}

function CustomerView({
  data,
  locationId,
  panel,
  onPanel,
  onLocation,
  onBack,
}: {
  data: DeskCustomer;
  locationId: string | null;
  panel: 'order' | 'service' | null;
  onPanel: (p: 'order' | 'service' | null) => void;
  onLocation: (id: string) => void;
  onBack: () => void;
}) {
  const { customer, locations, contacts, orders, service_requests } = data;
  const location = locations.find((l) => l.id === locationId) ?? locations[0] ?? null;
  const openOrders = orders.filter((o) => o.open);
  const pastOrders = orders.filter((o) => !o.open).slice(0, 8);

  return (
    <div className="space-y-4">
      {/* Customer card */}
      <div className="rounded-xl border border-brix-border bg-brix-surface p-4 shadow-card">
        <button type="button" onClick={onBack} className="mb-2 inline-flex items-center gap-1 text-xs text-brix-muted hover:text-brix-ink lg:hidden">
          <ArrowLeft size={14} aria-hidden /> Back to search
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display text-xl font-bold tracking-display-tight text-brix-ink">{customer.name}</h1>
            <p className="mt-0.5 font-mono text-xs text-brix-muted">
              {[
                customer.qbo_customer_id != null ? `Account #${customer.qbo_customer_id}` : null,
                customer.account_number ? `Acct ${customer.account_number}` : null,
                customer.store_code ? `Store ${customer.store_code}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => onPanel('order')} disabled={customer.ordering_paused || locations.length === 0} title="New order (N)">
              <ShoppingCart size={15} aria-hidden /> New order
              <kbd className="ml-1 rounded bg-white/20 px-1 font-mono text-[10px]">N</kbd>
            </Button>
            <Button variant="secondary" onClick={() => onPanel('service')} title="Service request (S)">
              <Wrench size={15} aria-hidden /> Service request
              <kbd className="ml-1 rounded border border-brix-border px-1 font-mono text-[10px]">S</kbd>
            </Button>
          </div>
        </div>

        {customer.ordering_paused && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-brix-warning/40 bg-brix-warning/5 px-3 py-2 text-sm text-brix-ink">
            <Lock size={15} className="mt-0.5 shrink-0 text-brix-warning" aria-hidden />
            <span>
              <span className="font-semibold">Ordering is paused on this account.</span> Do not take an
              order — tell the caller the office will call them back, and pass the call to the office.
            </span>
          </div>
        )}

        {/* Locations (sub-accounts) */}
        {locations.length > 1 && (
          <div className="mt-4">
            <label htmlFor="desk-loc" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brix-muted">
              Location ({locations.length})
            </label>
            <select
              id="desk-loc"
              value={location?.id ?? ''}
              onChange={(e) => onLocation(e.target.value)}
              className="h-10 w-full max-w-md rounded-lg border border-brix-border bg-brix-surface px-3 text-sm text-brix-ink focus:border-brix-primary focus:outline-none"
            >
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                  {l.address ? ` — ${l.address}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {location && (
            <div className="space-y-1 text-sm">
              <p className="flex items-start gap-2 text-brix-ink">
                <MapPin size={15} className="mt-0.5 shrink-0 text-brix-primary" aria-hidden />
                <span>
                  <span className="font-semibold">{location.name}</span>
                  {location.address && <span className="block text-brix-muted">{location.address}</span>}
                  {location.city_line && <span className="block text-brix-muted">{location.city_line}</span>}
                </span>
              </p>
              {location.phone && (
                <p className="flex items-center gap-2 text-brix-muted">
                  <Phone size={14} className="shrink-0 text-brix-primary" aria-hidden /> {location.phone}
                </p>
              )}
              <p className="flex items-start gap-2 text-brix-muted">
                <Truck size={14} className="mt-0.5 shrink-0 text-brix-primary" aria-hidden />
                <span>
                  {location.delivery_days
                    ? `Delivers ${location.delivery_days}${location.cutoff ? ` · order by ${location.cutoff} the day before` : ''}`
                    : 'No set delivery days — dispatch schedules it.'}
                  {location.next_delivery_date && (
                    <span className="block text-brix-ink">Next delivery day: {fmtDay(location.next_delivery_date)}</span>
                  )}
                </span>
              </p>
            </div>
          )}
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brix-muted">Contacts on file</p>
            {contacts.length === 0 ? (
              <p className="text-sm text-brix-muted">No contacts on file — take the caller's name and number.</p>
            ) : (
              <ul className="space-y-1.5">
                {contacts.slice(0, 6).map((c, i) => (
                  <li key={`${c.name}-${i}`} className="text-sm">
                    <span className="flex items-center gap-1.5 font-semibold text-brix-ink">
                      <User size={13} className="shrink-0 text-brix-primary" aria-hidden /> {c.name}
                      {c.role && <span className="font-normal text-brix-muted">· {c.role}</span>}
                    </span>
                    <span className="ml-5 flex flex-wrap gap-x-3 text-xs text-brix-muted">
                      {c.phone && (
                        <span className="inline-flex items-center gap-1"><Phone size={11} aria-hidden /> {c.phone}</span>
                      )}
                      {c.email && (
                        <span className="inline-flex items-center gap-1 break-all"><Mail size={11} aria-hidden /> {c.email}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {panel === 'order' && location && (
        <DeskOrderPanel
          customerId={customer.id}
          customerName={customer.name}
          locations={locations}
          initialLocationId={location.id}
          onClose={() => onPanel(null)}
        />
      )}
      {panel === 'service' && (
        <DeskServicePanel
          customerId={customer.id}
          locations={locations}
          initialLocationId={location?.id ?? null}
          onClose={() => onPanel(null)}
        />
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        <Card title="Open orders & upcoming deliveries" icon={<Truck size={15} aria-hidden />} count={openOrders.length}>
          {openOrders.length === 0 ? (
            <Empty>No open orders.</Empty>
          ) : (
            <ul className="divide-y divide-brix-border">
              {openOrders.map((o) => (
                <OrderRow key={o.id} o={o} />
              ))}
            </ul>
          )}
        </Card>
        <Card title="Open service requests" icon={<Wrench size={15} aria-hidden />} count={service_requests.length}>
          {service_requests.length === 0 ? (
            <Empty>No open service requests.</Empty>
          ) : (
            <ul className="divide-y divide-brix-border">
              {service_requests.map((r) => (
                <li key={r.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-brix-ink">{r.subject}</p>
                    <span className="shrink-0 rounded bg-brix-primary-tint px-1.5 py-0.5 text-[10px] font-semibold uppercase text-brix-primary">
                      {r.status === 'in_progress' ? 'In progress' : 'Open'}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-brix-muted">
                    {[serviceKindLabel(r.kind), r.location, `opened ${fmtDateTime(r.created_at)}`, r.sf_job_id ? `ticket ${r.sf_job_id}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card title="Recent orders" icon={<ClipboardList size={15} aria-hidden />} count={pastOrders.length}>
        {pastOrders.length === 0 ? (
          <Empty>No earlier orders in the portal.</Empty>
        ) : (
          <ul className="divide-y divide-brix-border">
            {pastOrders.map((o) => (
              <OrderRow key={o.id} o={o} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function OrderRow({ o }: { o: DeskOrder }) {
  const when = o.scheduled_date ?? o.requested_date;
  const items = o.lines.map((l) => `${l.quantity} × ${l.name}`).join(', ');
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-sm font-semibold text-brix-ink">{o.order_number ?? o.id.slice(0, 8)}</p>
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
            o.status === 'cancelled' || o.status === 'rejected'
              ? 'bg-brix-warning/10 text-brix-warning'
              : o.status === 'delivered' || o.status === 'invoiced'
                ? 'bg-brix-success/10 text-brix-success'
                : 'bg-brix-primary-tint text-brix-primary',
          )}
        >
          {orderStatusLabel(o.status)}
        </span>
      </div>
      {o.delivered_at ? (
        <p className="mt-0.5 text-xs text-brix-ink">Delivered {fmtDateTime(o.delivered_at)}</p>
      ) : when ? (
        <p className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-brix-ink">
          <CalendarDays size={12} aria-hidden />
          {o.scheduled_date ? 'Scheduled' : 'Requested'} {fmtDay(when)}
        </p>
      ) : null}
      <p className="mt-0.5 text-xs text-brix-muted">
        {[
          o.location,
          o.submitted_at ? `placed ${fmtDateTime(o.submitted_at)}` : null,
          o.placed_via,
          o.po_number ? `PO ${o.po_number}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {items && <p className="mt-1 text-xs text-brix-ink">{items}</p>}
    </li>
  );
}

function Card({ title, icon, count, children }: { title: string; icon: React.ReactNode; count: number; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-brix-border bg-brix-surface shadow-card">
      <div className="flex items-center gap-2 border-b border-brix-border px-4 py-2.5">
        <span className="text-brix-primary">{icon}</span>
        <h2 className="font-display text-sm font-bold text-brix-ink">{title}</h2>
        <span className="ml-auto rounded-full bg-brix-surface-alt px-2 py-0.5 text-[11px] font-semibold text-brix-muted">{count}</span>
      </div>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-4 py-6 text-center text-sm text-brix-muted">{children}</p>;
}
